import Modal, { ModalButton } from '@/components/common/Modal';
import PasswordField from '@/components/common/PasswordField';
import { changePasswordSchema } from '@/utils/validation';
import React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useChangePasswordMutation } from '@/store/api/userApi';
import { getApiErrorMessage, showToast } from '@/utils';
import { useNavigate } from 'react-router-dom';

type Props = {
  open: boolean;
  onClose: () => void;
};

type FormValues = z.infer<typeof changePasswordSchema>;

const ChangePasswordModal: React.FC<Props> = ({ open, onClose }) => {
  const [changePasswordMutation] = useChangePasswordMutation();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(changePasswordSchema),
    mode: 'onSubmit',
  });

  watch('newPassword'); // unchanged

  const onSubmit = async (data: FormValues) => {
    try {
      await changePasswordMutation(data).unwrap();
      navigate('/home');
      reset();
      onClose();
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Failed to change password'), 'error');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Change Password"
      showClose={false}
      closeOnBackdrop={false}
      description="Set a new password to secure your account"
      size="sm"
      footer={
        <>
          <ModalButton variant="secondary" onClick={() => reset()}>
            Reset
          </ModalButton>
          <ModalButton type="submit" form="change-password-form">
            Submit
          </ModalButton>
        </>
      }
    >
      <form id="change-password-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <PasswordField
          placeholder="Old password"
          {...register('oldPassword')}
          error={errors.oldPassword?.message}
        />
        <PasswordField
          placeholder="New password"
          {...register('newPassword')}
          error={errors.newPassword?.message}
        />
        <PasswordField
          placeholder="Confirm new password"
          {...register('confirmPassword')}
          error={errors.confirmPassword?.message}
        />
      </form>
    </Modal>
  );
};

export default ChangePasswordModal;
